import {FIELD_BIRDS} from './fieldAmbience';

/**
 * ГОЛОСА ПТИЦ НАД ПОЛЕМ. Пролет стаи или орла (см. scenes/FieldAmbience) иногда
 * озвучен: слышен крик этих птиц — в то время, когда игрок их видит.
 *
 * Пейзаж вокруг поля шире экрана, и стая большую часть пути летит за его
 * краем: крик оттуда звучал бы «из ниоткуда». Поэтому:
 *  - крик НАЧИНАЕТСЯ, только пока птицы в кадре, — в случайный момент этого
 *    времени;
 *  - птицы ушли из кадра, а крик еще звучит (файлы длятся по 5–8 секунд;
 *    птицы улетели, растаяли, игрок увел камеру) — он плавно ЗАТИХАЕТ;
 *  - пролет, который игрок так и не увидел, проходит молча.
 *
 * Кадр — то, что показывает камера, за вычетом того, что закрыто панелями
 * интерфейса (см. utils/effects/flightView).
 *
 * Сами звуки — отложенные (configs/deferredSounds): загрузку матча они не
 * держат, а пока не загружены, пролет просто идет молча. ГРОМКОСТЬ каждого
 * звука задается там же, у его строки.
 */
export const FIELD_FLIGHT_SOUNDS = Object.freeze({
  // С какой вероятностью пролет озвучен (если птицы попадут в кадр)
  chance: 0.5,
  // Когда начинается крик: доля времени, которое птицы проведут В КАДРЕ игрока,
  // случайно между min и max, — не в самом начале этого времени и не в самом
  // конце. Отсчет идет, только пока птицы в кадре. Чем меньше max, тем большая
  // часть крика успевает прозвучать до ухода птиц (дальше он затихает)
  startShare: Object.freeze({min: 0.1, max: 0.9}),
  // Птица «в кадре», когда она от края кадра не ближе этой доли размера
  // ячейки и хотя бы настолько непрозрачна (у края пейзажа птицы тают, орел
  // проступает и исчезает постепенно)
  viewInsetFactor: 0.5,
  minVisibleAlpha: 0.3,
  // Время птиц в кадре считается наперед, по нынешнему положению камеры. Если
  // по расчету они в кадр не попадут (или мелькнут), а игрок все же подвел к
  // ним камеру, окно для крика считается не короче этого, мс
  minViewWindowMs: 2000,
  // Птицы ушли из кадра, а крик еще звучит: за это время он затихает, мс
  // (вернулись в кадр, пока он не затих, — так же плавно возвращается)
  fadeOutMs: 1500,
});

// Голоса стай по цвету птиц (цвет у всей стаи один). Цвет без строки летит
// молча: у коричневых птиц голоса нет
const FLOCK_SOUND_IDS = Object.freeze([
  Object.freeze({color: FIELD_BIRDS.colors.black, soundIds: Object.freeze(['birds_crows_squawking', 'birds_flock_of_crows'])}),
  Object.freeze({color: FIELD_BIRDS.colors.white, soundIds: Object.freeze(['birds_seagulls_calling', 'birds_seagulls_in_flight'])}),
]);

const NO_SOUND_IDS = Object.freeze([]);

/**
 * Голоса стаи: один из них (случайный) прозвучит во время пролета
 * @param {number|null} color — цвет стаи (FIELD_BIRDS.colors)
 * @returns {string[]} имена отложенных звуков; пусто — стая летит молча
 */
export const getFlockSoundIds = (color) => (
  FLOCK_SOUND_IDS.find((entry) => entry.color === color)?.soundIds ?? NO_SOUND_IDS
);

/** Голоса орла над горой: клекот орла или крик ястреба */
export const EAGLE_SOUND_IDS = Object.freeze(['birds_eagle_screech', 'birds_hawk_in_flight']);
